export type BackHandler = () => boolean | void;

interface HandlerItem {
  id: string;
  handler: BackHandler;
  priority: number;
}

class BackButtonManager {
  private handlers: HandlerItem[] = [];

  /**
   * Register a back button handler.
   * Handlers with higher priority run first.
   * Returns an unregister function.
   */
  register(handler: BackHandler, priority: number = 100): () => void {
    const id = Math.random().toString(36).substring(2, 9);
    this.handlers.push({ id, handler, priority });
    // Sort descending by priority so highest priority runs first
    this.handlers.sort((a, b) => b.priority - a.priority);

    return () => {
      this.handlers = this.handlers.filter((h) => h.id !== id);
    };
  }

  /**
   * Attempt to handle a back action.
   * Iterates through handlers from highest priority to lowest.
   * If a handler returns true or undefined, the event is considered consumed.
   * If a handler returns false, the next handler in the queue is tried.
   */
  handleBack(): boolean {
    if (this.handlers.length === 0) {
      return false;
    }

    // Clone array to prevent issues if a handler unregisters itself synchronously
    const queue = [...this.handlers];

    for (const item of queue) {
      try {
        const result = item.handler();
        if (result !== false) {
          return true; // Successfully handled!
        }
      } catch (err) {
        console.error('[BackButtonManager] Error in back handler:', err);
      }
    }

    return false;
  }

  hasHandlers(): boolean {
    return this.handlers.length > 0;
  }
}

export const backButtonManager = new BackButtonManager();
